import axios from 'axios';

const API_BASE_URL = '/api';

// Create axios instance with auth
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token and tenant site ID
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Send active site ID for multi-tenant scoping
  const activeSiteId = localStorage.getItem('activeSiteId');
  if (activeSiteId) {
    config.headers['x-site-id'] = activeSiteId;
  }
  return config;
});

// Add response interceptor to handle module access errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 403 && error.response.data?.module) {
      // This is a module access error
      const moduleError = error.response.data;
      
      // Store the error for UI components to handle
      localStorage.setItem('moduleAccessError', JSON.stringify(moduleError));
      
      // Redirect to the module access error page
      window.location.href = '/module-access-error';
      
      // Prevent the error from propagating
      return new Promise(() => {});
    }
    return Promise.reject(error);
  }
);

// Types
export interface InvoiceItem {
  description: string;
  quantity: number;
  unitPrice: number;
  amount: number;
  type: 'base' | 'premium_feature' | 'discount';
  module: string;
}

export interface BillingInfo {
  companyName: string;
  contactName: string;
  billingAddress: {
    street: string;
    city: string;
    postcode: string;
    country: string;
  };
  contactEmail: string;
  contactPhone?: string;
  vatNumber?: string;
}

export interface CompanyInfo {
  name: string;
  address: {
    street: string;
    city: string;
    postcode: string;
    country: string;
  };
  email: string;
  phone: string;
  vatNumber: string;
  companyNumber: string;
}

export interface Invoice {
  _id: string;
  invoiceNumber: string;
  siteId: {
    _id: string;
    name: string;
    code: string;
    email?: string;
    phone?: string;
  };
  billingPeriod: {
    startDate: string;
    endDate: string;
  };
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';
  currency: string;
  items: InvoiceItem[];
  subtotal: number;
  taxAmount: number;
  taxRate: number;
  totalAmount: number;
  paidAmount: number;
  balanceAmount: number;
  dueDate: string;
  issueDate: string;
  paidDate?: string;
  paymentMethod: 'card' | 'bank_transfer' | 'cheque' | 'cash';
  paymentStatus: 'pending' | 'partial' | 'fully_paid' | 'refunded';
  billingInfo: BillingInfo;
  companyInfo: CompanyInfo;
  notes: string;
  metadata: {
    generatedBy: {
      _id: string;
      firstName: string;
      lastName: string;
      email: string;
    };
    autoGenerated: boolean;
    sentEmail: boolean;
    emailSentAt?: string;
    lastReminderSent?: string;
    reminderCount: number;
  };
  createdAt: string;
  updatedAt: string;
}

export interface InvoicesResponse {
  invoices: Invoice[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface InvoiceStatistics {
  byStatus: Array<{
    _id: string;
    count: number;
    totalAmount: number;
    paidAmount: number;
    balanceAmount: number;
  }>;
  total: {
    totalInvoices: number;
    totalAmount: number;
    totalPaid: number;
    totalBalance: number;
  };
}

export interface GenerateInvoicesResponse {
  message: string;
  generatedInvoices: Invoice[];
  errors: Array<{
    site: string;
    error: string;
  }>;
  summary: {
    totalInvoices: number;
    totalAmount: number;
    errors: number;
  };
}

// API Functions
export const invoicesAPI = {
  // Get invoices for a site
  getSiteInvoices: (siteId: string, params?: {
    page?: number;
    limit?: number;
    status?: string;
    year?: number;
  }) => api.get<InvoicesResponse>(`/invoices/sites/${siteId}/invoices`, { params }),

  // Get all invoices (super admin only)
  getAllInvoices: (params?: {
    page?: number;
    limit?: number;
    status?: string;
    year?: number;
    month?: number;
    siteId?: string;
  }) => api.get<InvoicesResponse>(`/invoices`, { params }),

  // Get single invoice
  getInvoice: (invoiceId: string) => 
    api.get<Invoice>(`/invoices/${invoiceId}`),

  // Generate monthly invoices for all sites (super admin only)
  generateMonthlyInvoices: (year: number, month: number) => 
    api.post<GenerateInvoicesResponse>(`/invoices/generate-monthly`, { year, month }),

  // Generate invoice for specific site (super admin only)
  generateSiteInvoice: (siteId: string, year: number, month: number) => 
    api.post<{ message: string; invoice: Invoice }>(`/invoices/sites/${siteId}/invoices/generate`, { year, month }),

  // Update invoice status
  updateInvoiceStatus: (invoiceId: string, status: string, notes?: string) => 
    api.put<{ message: string; invoice: Invoice }>(`/invoices/${invoiceId}/status`, { status, notes }),

  // Mark invoice as paid (super admin only)
  markInvoiceAsPaid: (invoiceId: string, paymentMethod: string, paidAmount?: number) => 
    api.put<{ message: string; invoice: Invoice }>(`/invoices/${invoiceId}/mark-paid`, { paymentMethod, paidAmount }),

  // Get invoice statistics for a site
  getSiteInvoiceStatistics: (siteId: string, year?: number) => 
    api.get<InvoiceStatistics>(`/invoices/sites/${siteId}/invoices/statistics`, { params: { year } }),

  // Get all invoice statistics (super admin only)
  getInvoiceStatistics: (year?: number) => 
    api.get<InvoiceStatistics>(`/invoices/statistics`, { params: { year } })
};
